/**
 * DraggableSection.tsx - Draggable Section Wrapper Component
 *
 * This component wraps fundraiser sections to make them draggable within the
 * layout builder with visual feedback and drag state management.
 *
 * Key Features:
 * - Drag-and-drop functionality using @dnd-kit library
 * - Visual drag indicators and feedback
 * - Drag state management and styling
 * - Section identification and labeling
 * - Hover effects and interaction states
 * - Accessibility support for drag operations
 *
 * Drag Behavior:
 * - Drag handle with grip icon for clear interaction
 * - Visual feedback during drag operations
 * - Smooth transitions and animations
 * - Drop zone highlighting and validation
 * - Snap-to-grid positioning
 *
 * Visual States:
 * - Default: Normal section appearance
 * - Hover: Highlighted with drag affordances
 * - Dragging: Elevated with rotation and scaling
 * - Disabled: Grayed out when drag is not available
 *
 * Props:
 * - id: string - Unique identifier for the section
 * - children: React.ReactNode - Section content to wrap
 * - disabled?: boolean - Whether dragging is disabled
 *
 * Dependencies: @dnd-kit/sortable, styled-components, Lucide icons
 */

import React, { useState } from "react";
import styled from "styled-components";
import { GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SectionContainer = styled.div<{
  isDragging: boolean;
  isActive: boolean;
  disabled?: boolean;
  zoneType: string;
  isDragMode?: boolean;
}>`
  position: relative;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isDragging ? 0.8 : 1)};
  z-index: ${(props) => (props.isDragging ? 1000 : 1)};
  cursor: ${(props) =>
    props.disabled ? "default" : props.isDragging ? "grabbing" : "grab"};
  border-radius: 8px;
  background: ${(props) =>
    props.disabled
      ? "transparent"
      : props.isActive && !props.isDragMode
        ? "rgba(59, 130, 246, 0.08)"
        : "transparent"};
  border: ${(props) =>
    props.disabled
      ? "none"
      : props.isActive && !props.isDragMode
        ? "2px solid rgba(59, 130, 246, 0.3)"
        : "2px solid transparent"};
  padding: ${(props) => (props.disabled ? "0" : "4px")};
  margin: 2px 0;
  will-change: ${(props) =>
    props.isDragging ? "transform, opacity, z-index" : "auto"};

  /* Prevent content from shifting during transform operations */
  isolation: ${(props) => (props.isDragging ? "isolate" : "auto")};

  /* Prevent layout shifts during drag operations */
  contain: ${(props) => (props.isDragging ? "layout style" : "none")};

  /* Enhanced hover effects for draggable sections */
  &:hover:not([data-disabled="true"]):not([data-drag-mode="true"]) {
    background: rgba(59, 130, 246, 0.05) !important;
    border-color: rgba(59, 130, 246, 0.3) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.2) !important;
    cursor: grab;

    .section-label {
      opacity: 1;
      visibility: visible;
      transform: scale(1);
    }

    .drag-handle {
      opacity: 1;
      visibility: visible;
    }

    .hide-button {
      opacity: 1;
      visibility: visible;
    }
  }

  /* Active drag state */
  &[data-is-dragging="true"]:not([data-disabled="true"]) {
    opacity: 0.9 !important;
    transform: rotate(2deg) scale(1.05) !important;
    z-index: 1000 !important;
    background: rgba(255, 255, 255, 0.98) !important;
    box-shadow: 0 12px 40px rgba(59, 130, 246, 0.4) !important;
    backdrop-filter: blur(12px);
    border: 2px solid #3b82f6 !important;
    cursor: grabbing !important;

    .section-label {
      opacity: 1 !important;
      visibility: visible !important;
    }
  }

  /* Adjust width based on zone type */
  width: ${(props) => {
    switch (props.zoneType) {
      case "full-width":
        return "100%";
      case "left-column":
        return "100%";
      case "right-column":
        return "100%";
      default:
        return "100%";
    }
  }};
`;

const SectionLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  opacity: 0;
  visibility: hidden;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.3s ease;
  z-index: 10;
  pointer-events: none;
  backdrop-filter: blur(4px);
  transform: scale(0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const DragHandle = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  visibility: hidden;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.3s ease;
  z-index: 10;
  pointer-events: none;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const HideButton = styled.button`
  position: absolute;
  top: 8px;
  right: 40px;
  opacity: 0;
  visibility: hidden;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.3s ease;
  z-index: 10;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;
interface DraggableSectionProps {
  sectionId: string;
  children: React.ReactNode;
  disabled?: boolean;
  sectionName?: string;
  zoneType?: string;
  isActive?: boolean;
  isDragMode?: boolean;
  onHide?: () => void;
}

export const DraggableSection: React.FC<DraggableSectionProps> = ({
  sectionId,
  children,
  disabled = false,
  sectionName,
  zoneType = "full-width",
  isActive = false,
  isDragMode = false,
  onHide,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sectionId,
    disabled,
    data: {
      type: "section",
      sectionId,
      zoneType,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleMouseEnter = () => {
    if (!disabled && !isDragMode) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isDragMode) {
      setIsHovered(false);
    }
  };

  return (
    <SectionContainer
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
      isActive={!disabled && (isActive || isHovered)}
      disabled={disabled}
      zoneType={zoneType}
      isDragMode={isDragMode}
      data-disabled={disabled}
      data-is-dragging={isDragging}
      data-drag-mode={isDragMode}
      onMouseEnter={!isDragMode ? handleMouseEnter : undefined}
      onMouseLeave={!isDragMode ? handleMouseLeave : undefined}
      {...(!disabled ? attributes : {})}
      {...(!disabled ? listeners : {})}
    >
      {!disabled && (
        <>
          <SectionLabel className="section-label">
            {sectionName || sectionId}
          </SectionLabel>
          <HideButton
            className="hide-button"
            onClick={(e) => {
              e.stopPropagation();
              if (onHide) onHide();
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            </svg>
          </HideButton>
          <DragHandle className="drag-handle">
            <GripVertical size={14} />
          </DragHandle>
        </>
      )}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          transform: isDragging ? "translateZ(0)" : "none",
          backfaceVisibility: isDragging ? "hidden" : "visible",
          pointerEvents: isDragMode && !isDragging ? "none" : "auto",
        }}
      >
        {children}
      </div>
    </SectionContainer>
  );
};
